// web/lib/supabaseClient.ts
import { createClient, SupabaseClient } from '@supabase/supabase-js';

// Fallback values to prevent createClient from throwing during build/SSR
const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL || 'https://placeholder.supabase.co';
const supabaseAnonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY || 'placeholder-key';

// Log warning in development if env vars are missing
if (typeof window !== 'undefined' && (!process.env.NEXT_PUBLIC_SUPABASE_URL || !process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY)) {
  console.warn(
    'Supabase URL or anon key is missing. ' +
      'Set NEXT_PUBLIC_SUPABASE_URL and NEXT_PUBLIC_SUPABASE_ANON_KEY in your environment.'
  );
}

/**
 * Custom fetch that routes Supabase Auth requests through our API proxy
 * to avoid CORS when the app origin is not in Supabase's allowed URL list.
 */
function createAuthProxiedFetch(): (input: RequestInfo | URL, init?: RequestInit) => Promise<Response> {
  const authPath = '/auth/v1/';
  return (input: RequestInfo | URL, init?: RequestInit) => {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
    if (typeof window !== 'undefined' && url.includes(authPath)) {
      const idx = url.indexOf(authPath);
      const afterAuth = url.slice(idx + authPath.length);
      const [path, query] = afterAuth.split('?');
      const proxyUrl = `${window.location.origin}/api/auth-proxy/${path}${query ? `?${query}` : ''}`;
      const req = input instanceof Request ? input : null;
      const opts: RequestInit = init || {};
      if (req) {
        opts.method ??= req.method;
        opts.headers ??= req.headers;
        opts.body ??= req.body;
      }
      return fetch(proxyUrl, opts);
    }
    return fetch(input, init);
  };
}

// Single client instance for use in client components
// Note: Will use placeholder values during build, but real values at runtime
export const supabase: SupabaseClient = createClient(supabaseUrl, supabaseAnonKey, {
  global: {
    fetch: createAuthProxiedFetch(),
  },
});
